import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ClipboardList,
  Download,
  Loader2,
  Info,
  Search,
  Upload,
  Users,
} from 'lucide-react';
import {
  ojtScorecardApi,
  OjtBatchDetail,
  OjtBatchSummary,
  OjtScorecardMeta,
  OjtUploadError,
} from '../api';

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type Toast = {
  type: 'success' | 'error';
  message: string;
};

const OjtScorecards: React.FC = () => {
  const [meta, setMeta] = useState<OjtScorecardMeta | null>(null);
  const [batches, setBatches] = useState<OjtBatchSummary[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [batchDetail, setBatchDetail] = useState<OjtBatchDetail | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<OjtUploadError[]>([]);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<Toast | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = useCallback((message: string, type: Toast['type']) => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [metaResponse, batchResponse] = await Promise.all([
          ojtScorecardApi.getMeta(),
          ojtScorecardApi.listBatches(),
        ]);
        if (!mounted) return;
        setMeta(metaResponse);
        setBatches(batchResponse);
        if (batchResponse.length > 0) {
          setSelectedBatchId((prev) => prev ?? batchResponse[0].batch_id);
        }
      } catch (err: any) {
        if (!mounted) return;
        setError(err.response?.data?.message || err.message || 'Unable to load data.');
      } finally {
        if (mounted) setInitialLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedBatchId) {
      setBatchDetail(null);
      return;
    }
    let mounted = true;
    setDetailLoading(true);
    setError(null);
    setUploadErrors([]);
    (async () => {
      try {
        const detail = await ojtScorecardApi.getBatchDetail(selectedBatchId);
        if (!mounted) return;
        setBatchDetail(detail);
      } catch (err: any) {
        if (!mounted) return;
        setError(err.response?.data?.message || err.message || 'Unable to load batch detail.');
        setBatchDetail(null);
      } finally {
        if (mounted) setDetailLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedBatchId]);

  const filteredTrainees = useMemo(() => {
    if (!batchDetail) return [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return batchDetail.trainees;
    return batchDetail.trainees.filter(
      (trainee) =>
        trainee.trainee_name.toLowerCase().includes(query) ||
        trainee.login_id.toLowerCase().includes(query) ||
        (trainee.overall_band ?? '').toLowerCase().includes(query) ||
        (trainee.citation ?? '').toLowerCase().includes(query)
    );
  }, [batchDetail, searchQuery]);

  const handleDownloadTemplate = async () => {
    if (!selectedBatchId || !batchDetail) return;
    try {
      const blob = await ojtScorecardApi.downloadTemplate(selectedBatchId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `OJT_Scorecard_${batchDetail.batch.code}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to download template.');
    }
  };

const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
  if (!event.target.files || event.target.files.length === 0 || !selectedBatchId) {
    return;
  }
  const file = event.target.files[0];
  try {
    setUploading(true);
    setUploadErrors([]);
    setError(null);
    const response = await ojtScorecardApi.uploadScores(selectedBatchId, file);
    if (!response.success) {
      setUploadErrors((response as any).errors ?? []);
      setError(response.message ?? 'Upload failed.');
      showToast(response.message ?? 'Upload failed. Please check the error list.', 'error');
      return;
    }
    const detail = await ojtScorecardApi.getBatchDetail(selectedBatchId);
    setBatchDetail(detail);
    showToast('Evaluation sheet uploaded successfully.', 'success');
  } catch (err: any) {
    const serverErrors: OjtUploadError[] | undefined = err.response?.data?.errors;
    if (serverErrors) {
      setUploadErrors(serverErrors);
    }
    const message = err.response?.data?.message || err.message || 'Upload failed.';
    setError(message);
    showToast(message, 'error');
  } finally {
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }
};

  const handleUploadClick = () => {
    if (!batchDetail?.can_upload) return;
    fileInputRef.current?.click();
  };

  if (initialLoading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow p-8 flex items-center justify-center">
          <div className="animate-spin h-10 w-10 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  const criteria = meta?.criteria ?? [];
  const maxTotal = meta?.score_config?.max_total ?? 50;

  const renderToast = () =>
    toast ? (
      <div
        className={`fixed inset-x-0 top-4 z-50 mx-auto w-full max-w-md rounded-md border px-4 py-3 text-sm shadow-lg ${
          toast.type === 'success'
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-rose-200 bg-rose-50 text-rose-800'
        }`}
      >
        {toast.message}
      </div>
    ) : null;

  return (
    <div className="p-6 space-y-6">
      {renderToast()}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary-600" />
            OJT Scorecards
          </h1>
          <p className="text-slate-600">
            Download batch templates, fill scores offline, and upload validated evaluations back into the platform.
          </p>
        </div>
        {meta?.template_version && (
          <div className="text-sm text-slate-500">
            Template version <span className="font-semibold text-slate-700">{meta.template_version}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="border border-rose-200 bg-rose-50 text-rose-800 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 mt-0.5" />
          <div>
            <p className="font-semibold">Something went wrong</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {uploadErrors.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-rose-100">
          <div className="flex items-center gap-2 mb-3 text-rose-600">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="font-semibold">Upload errors</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-slate-700">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4">Row</th>
                  <th className="text-left py-2 pr-4">Column</th>
                  <th className="text-left py-2 pr-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {uploadErrors.map((uploadError, index) => (
                  <tr key={`${uploadError.row}-${uploadError.column}-${index}`} className="border-b last:border-0">
                    <td className="py-2 pr-4">{uploadError.row}</td>
                    <td className="py-2 pr-4">{uploadError.column ?? '—'}</td>
                    <td className="py-2 pr-4">{uploadError.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {meta && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-primary-600" />
            <h2 className="text-base font-semibold text-slate-900">Trainer instructions</h2>
          </div>
          <ol className="list-decimal pl-5 space-y-1.5 text-xs text-slate-600 leading-relaxed">
            {meta.instructions.map((instruction) => (
              <li key={instruction} className="pl-1">
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="space-y-6">
        <section className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="batchSelect" className="text-sm font-medium text-slate-700">
                Select batch
              </label>
              <select
                id="batchSelect"
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={selectedBatchId ?? ''}
                onChange={(event) => setSelectedBatchId(Number(event.target.value) || null)}
              >
                {batches.map((batch) => (
                  <option key={batch.batch_id} value={batch.batch_id}>
                    {batch.batch_code} · {batch.program_name ?? 'Program'}
                  </option>
                ))}
                {!batches.length && <option value="">No batches assigned</option>}
              </select>
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-sm text-primary-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading sheet…
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleDownloadTemplate}
                disabled={!batchDetail}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none"
              >
                <Download className="h-4 w-4" />
                Download template
              </button>
              <button
                onClick={handleUploadClick}
                disabled={!batchDetail?.can_upload || uploading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                <Upload className="h-4 w-4" />
                {uploading ? 'Uploading…' : 'Upload completed sheet'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          {batchDetail && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-3 rounded-xl bg-slate-50">
                <p className="text-xs uppercase text-slate-500">Batch code</p>
                <p className="text-base font-semibold text-slate-900">{batchDetail.batch.code}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-50">
                <p className="text-xs uppercase text-slate-500">Total trainees</p>
                <p className="text-base font-semibold text-slate-900">{batchDetail.batch.total_trainees}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-50">
                <p className="text-xs uppercase text-slate-500">Evaluated</p>
                <p className="text-base font-semibold text-emerald-600">{batchDetail.batch.evaluated_trainees}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-50">
                <p className="text-xs uppercase text-slate-500">Last upload</p>
                <p className="text-base font-semibold text-slate-900">
                  {formatDateTime(batchDetail.batch.last_uploaded_at)}
                </p>
              </div>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-slate-600 flex items-center gap-2">
              <Users className="h-4 w-4 text-primary-500" />
              <span>
                {filteredTrainees.length} of {batchDetail?.trainees.length ?? 0} trainees
              </span>
            </div>
            <div className="flex items-center gap-3">
              {detailLoading && (
                <span className="text-xs text-primary-600 animate-pulse">Refreshing…</span>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Search by name, email, band, or citation"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="overflow-auto border border-slate-200 rounded-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Trainee</th>
                  <th className="px-4 py-2 text-left font-semibold">Login ID</th>
                  <th className="px-4 py-2 text-left font-semibold">Total</th>
                  <th className="px-4 py-2 text-left font-semibold">Achieved %</th>
                  <th className="px-4 py-2 text-left font-semibold">Band</th>
                  <th className="px-4 py-2 text-left font-semibold">Criteria</th>
                  {criteria.map((criterion) => (
                    <th key={`crit-${criterion.code}`} className="px-4 py-2 text-center font-semibold">
                      {criterion.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y text-slate-800">
                {filteredTrainees.map((trainee) => {
                  const scoreMap = new Map(trainee.scores.map((score) => [score.code, score]));
                  return (
                    <tr key={trainee.trainee_id} className="align-top hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold">{trainee.trainee_name}</td>
                      <td className="px-4 py-3 text-slate-500">{trainee.login_id}</td>
                      <td className="px-4 py-3">
                        {trainee.total_score != null ? `${trainee.total_score}/${maxTotal}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {trainee.total_score != null
                          ? `${((trainee.total_score / maxTotal) * 100).toFixed(2)}%`
                          : '—'}
                      </td>
                      <td className="px-4 py-3">{trainee.overall_band ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {trainee.citation || <span className="text-slate-400">—</span>}
                      </td>
                      {criteria.map((criterion) => {
                        const scoreRow = scoreMap.get(criterion.code);
                        return (
                          <td
                            key={`row-${trainee.trainee_id}-${criterion.code}`}
                            className="px-4 py-3 text-center font-semibold text-primary-600"
                          >
                            {scoreRow?.score ?? '—'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {!filteredTrainees.length && (
                  <tr>
                    <td colSpan={7 + criteria.length} className="text-center text-slate-500 py-6">
                      No trainees match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

      </div>

      <section className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary-600" />
            <h3 className="text-base font-semibold text-slate-900">Score interpretation</h3>
          </div>
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            Max {meta?.score_config.max_total ?? 50} pts
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {meta?.score_bands.map((band) => (
            <div
              key={band.label}
              className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3 flex flex-col gap-1"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-900">{band.label}</span>
                <span className="text-[11px] font-bold text-primary-600 px-2 py-0.5 rounded-full bg-primary-50">
                  {band.min} – {band.max}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wide">Readiness notes</p>
              <p className="text-xs text-slate-600 leading-snug">{band.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Info className="h-5 w-5 text-primary-600" />
          <h3 className="text-base font-semibold text-slate-900">Criteria guide</h3>
        </div>
        <div className="overflow-auto border border-slate-200 rounded-xl">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-semibold w-40">Criterion</th>
                <th className="px-3 py-2 text-left font-semibold w-56">Focus</th>
                {[5, 4, 3, 2, 1].map((score) => (
                  <th key={`score-${score}`} className="px-3 py-2 text-left font-semibold">
                    {score === 5
                      ? "Excellent (5)"
                      : score === 4
                      ? "Very Good (4)"
                      : score === 3
                      ? "Satisfactory (3)"
                      : score === 2
                      ? "Needs Improvement (2)"
                      : "Unsatisfactory (1)"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y text-slate-700">
              {meta?.criteria.map((criterion) => (
                <tr key={criterion.code} className="align-top">
                  <td className="px-3 py-2 font-semibold text-slate-900">
                    {criterion.code}. {criterion.title}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{criterion.focus}</td>
                  {[5, 4, 3, 2, 1].map((score) => (
                    <td key={`crit-${criterion.code}-${score}`} className="px-3 py-2 text-slate-600 leading-snug">
                      {criterion.ratingGuide.find((rating) => rating.score === score)?.description ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default OjtScorecards;
